<template>
  <FormGroup :label="label" :inputId="idCalc" :errors="errorsCalc" :successes="successes">
    <template #checkbox>
      <div v-for="(distro, i) in distributions" :key="i" class="d-flex">
        <FormCheckbox
          :value="selectedDistro === distro"
          :label="capitalize(distro)"
          @change="(checked) => onDistroCheck(distro, checked)"
          :disabled="disabled"
          class="pt-4 pr-4"
        />
        <FormSelect
          v-if="versions.list[distro]"
          name="version"
          :label="$t('global.version')"
          :value="getValue(distro)"
          @input="(id) => onVersionSelect(id, distro)"
          :disabled="distro !== selectedDistro || disabled"
          class="w-100"
        >
          <option
            v-for="(version, j) in versions.list[distro]"
            :key="`${distro}-${j}`"
            :value="version.id"
          >
            {{ getVersionName(version) }}
          </option>
        </FormSelect>
        <div class="ml-2 addon-checkbox">
          <div v-if="showAddons(distro)">
            <FormCheckbox
              v-for="(addon, index) in addonList"
              :key="index"
              :label="getAddonPrice(addon)"
              @change="(checked) => onAddonSelect(addon, checked)"
              class="pt-4"
            />
          </div>
        </div>
      </div>
    </template>
  </FormGroup>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import { capitalize } from "leatherman-js/utils/formatters/text";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormGroup from "@/components/display/form/FormGroup";
import FormMixin from "@/components/display/form/FormMixin";
import FormSelect from "@/components/display/form/FormSelect";
import { moneyFormat } from "leatherman-js/utils/formatters/money";

export default {
  name: "FormOSSelect",
  mixins: [FormMixin],
  components: {
    FormGroup,
    FormCheckbox,
    FormSelect,
  },
  props: {
    distributions: Array,
    oses: Array,
    addons: Array,
    disabled: Boolean,
  },
  setup(props, { emit }) {
    const selectedDistro = ref(null);
    const selectedVersionId = ref(null);
    const versions = reactive({
      list: {},
      addons: [],
    });

    // formatted version name
    const getVersionName = ({ os }) => `${os.distribution} ${os.version} ${os.architecture}`;
    // hide or show addon list depending on which distro is selected
    const showAddons = (distro) => !!addonList.value.length && distro === selectedDistro.value;
    // formatted addon price
    const getAddonPrice = ({ name, monthlyFee }) => `${name} ${moneyFormat(monthlyFee)}`;

    // a computed list of addons that are compatible with the selected distro and os version
    const addonList = computed(() => {
      if (!versions.list[selectedDistro.value]) {
        return [];
      }

      const version = versions.list[selectedDistro.value].find(
        ({ id }) => id === selectedVersionId.value
      );
      if (!version) {
        return [];
      }

      return props.addons.filter((addon) => {
        return addon.osIds.includes(version?.os?.id);
      });
    });

    // when a distro is checked, emit the first version in the list by default
    // when unchecked, emit null to clear
    const onDistroCheck = (distro, checked) => {
      selectedVersionId.value = null;
      if (checked) {
        selectedDistro.value = distro;
        emit("input", versions.list[distro][0].id);
      } else {
        selectedDistro.value = null;
        emit("input", null);
      }
    };

    // emit what os version was selected
    // also clear and emit what addons might have been selected (different addons are available per os version)
    const onVersionSelect = (versionId) => {
      selectedVersionId.value = Number(versionId);
      versions.addons = [];
      emit("input", selectedVersionId.value);
      emit("addonSelect", versions.addons);
    };

    // if an addon is checked/unchecked, add or remove it from the list, and emit
    const onAddonSelect = ({ id }, checked) => {
      const index = versions.addons.findIndex((addonId) => addonId === id);
      // add or remove key from list depending on whether checkbox is checked
      if (checked && index === -1) {
        versions.addons.push(id);
      } else {
        versions.addons.splice(index, 1);
      }
      emit("addonSelect", versions.addons);
    };

    // this is a hacky way to define the default selected os version
    // because there's a dynamically generated list of dropdowns, this was more complex than I thought
    const getValue = (distro) => {
      if (distro === selectedDistro.value && selectedVersionId.value) {
        return selectedVersionId.value;
      }

      return versions.list[distro][0].id;
    };

    onMounted(() => {
      // populate the form with distros and available os versions per distro
      const versionList = {};
      props.distributions.forEach((distro) => {
        versionList[distro] = props.oses.filter(({ os }) => os.distribution === distro);
      });
      versions.list = versionList;
    });

    return {
      onDistroCheck,
      onVersionSelect,
      onAddonSelect,
      selectedDistro,
      versions,
      getVersionName,
      capitalize,
      getAddonPrice,
      addonList,
      selectedVersionId,
      showAddons,
      getValue,
    };
  },
};
</script>
<style lang="scss" scoped>
.addon-checkbox {
  min-width: 60px;
}
</style>
